import { FormEvent, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { AVATAR_STYLES, Avatar, AvatarStyleId, avatarUrl } from "@/components/Avatar";
import { BenchmarkCard } from "@/components/BenchmarkCard";
import { ScoreCard } from "@/components/ScoreCard";
import {
  balanceErrorLabel,
  formatBalance,
} from "@/components/PortalBalanceCard";
import { PlatformBadge, PlatformLogo, platformLabel } from "@/components/PlatformLogo";
import { SectionHeader } from "@/components/SectionHeader";
import { Skeleton } from "@/components/Skeleton";
import { formatLastSeen } from "@/lib/presence";
import type {
  BrokerBenchmarks,
  CurrentUser,
  Placement,
  Platform,
  PortalBalance,
  PortalBalances,
  StoredCredential,
  TeamMember,
  TeamSession,
  UserRole,
  UserStatus,
} from "@/types";
import { roleLabel } from "@/types";

const ROLE_STYLES: Record<UserRole, string> = {
  shareholder: "border-violet-500/40 bg-violet-500/10 text-violet-300",
  executive_manager: "border-accent-500/30 bg-accent-500/10 text-accent-400",
  // Operations Manager shares the accent palette with the executive manager
  // but at lower saturation — hints "right hand", not "in charge".
  operations_manager: "border-accent-500/25 bg-accent-500/[0.06] text-accent-300",
  lead_broker: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
  broker: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  member: "border-slate-500/30 bg-slate-500/10 text-slate-300",
};

const STATUS_STYLES: Record<UserStatus, string> = {
  active: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400",
  disabled: "border-rose-500/40 bg-rose-500/15 text-rose-200",
};

const ROLE_DESCRIPTION: Record<UserRole, string> = {
  shareholder:
    "Shareholder tier — full access, immune, and never measured. Sits above the Executive Manager.",
  executive_manager: "Full access to everything in the agency.",
  operations_manager:
    "Executive Manager's right hand — manages the whole team except the executive manager.",
  lead_broker: "Can invite members and see their work.",
  broker: "Handles leads — has access to Hot Offers.",
  member: "Publishes listings for the agency.",
};

function formatDate(s: string | null) {
  if (!s) return "—";
  return new Date(s).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function formatDateOnly(s: string | null) {
  if (!s) return "—";
  return new Date(s).toLocaleDateString(undefined, { dateStyle: "medium" });
}

/** Compact one-line "22 Jul, 06:00" — keeps table cells from wrapping. */
function formatShort(s: string | null) {
  if (!s) return "—";
  const d = new Date(s);
  return (
    d.toLocaleDateString(undefined, { day: "numeric", month: "short" }) +
    ", " +
    d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
  );
}

function shortUA(ua: string | null): string {
  if (!ua) return "—";
  const browser =
    /Chrome\/\d+/.exec(ua)?.[0] ||
    /Firefox\/\d+/.exec(ua)?.[0] ||
    /Safari\/\d+/.exec(ua)?.[0] ||
    /Edg\/\d+/.exec(ua)?.[0] ||
    null;
  const os =
    /(Mac OS X [\d_]+)/.exec(ua)?.[0]?.replace(/_/g, ".") ||
    /(Windows NT [\d.]+)/.exec(ua)?.[0] ||
    /(Android \d+)/.exec(ua)?.[0] ||
    /(iPhone|iPad|Linux x86_64|Linux)/.exec(ua)?.[0] ||
    null;
  return [browser, os].filter(Boolean).join(" · ") || ua.slice(0, 60);
}

function formatPrice(p: Placement): string {
  if (!p.price) return "—";
  const sym = p.currency === "USD" ? "$" : p.currency === "GEL" ? "₾" : p.currency ? p.currency + " " : "";
  return `${sym}${Number(p.price).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

interface PlacementList {
  items: Placement[];
  total: number;
  limit: number;
  offset: number;
}

export function Profile() {
  const { user: viewer, refresh } = useAuth();

  const [member, setMember] = useState<TeamMember | null>(null);
  const [sessions, setSessions] = useState<TeamSession[]>([]);
  const [placements, setPlacements] = useState<Placement[]>([]);
  const [placementTotal, setPlacementTotal] = useState(0);
  const [credentials, setCredentials] = useState<StoredCredential[]>([]);
  const [benchmarks, setBenchmarks] = useState<BrokerBenchmarks | null>(null);

  const [loadErr, setLoadErr] = useState<string | null>(null);

  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [nameSaving, setNameSaving] = useState(false);
  const [nameErr, setNameErr] = useState<string | null>(null);

  const [avatarOpen, setAvatarOpen] = useState(false);

  async function saveAvatar(url: string | null) {
    try {
      // Backend treats "" as the clear-avatar sentinel; null leaves it
      // untouched. We map our null-means-remove UX to "" on the wire.
      await api(`/api/auth/me`, {
        method: "PATCH",
        json: { avatar_url: url ?? "" },
      });
      await refresh();
      if (viewer) {
        const m = await api<TeamMember>(`/api/team/${viewer.id}`);
        setMember(m);
      }
      setAvatarOpen(false);
    } catch (err) {
      setNameErr(err instanceof ApiError ? err.message : "Failed to save avatar");
    }
  }

  async function loadAll(u: CurrentUser) {
    try {
      const m = await api<TeamMember>(`/api/team/${u.id}`);
      setMember(m);
      setNameDraft(m.full_name || "");
    } catch (err) {
      setLoadErr(err instanceof Error ? err.message : "Failed to load profile");
      return;
    }

    try {
      const data = await api<PlacementList>(`/api/placements?user_id=${u.id}&limit=5`);
      setPlacements(data.items);
      setPlacementTotal(data.total);
    } catch {
      setPlacements([]);
    }

    try {
      const b = await api<BrokerBenchmarks>(`/api/team/${u.id}/benchmarks`);
      setBenchmarks(b);
    } catch {
      setBenchmarks(null);
    }

    try {
      const rows = await api<TeamSession[]>(`/api/team/${u.id}/sessions?limit=10`);
      setSessions(rows);
    } catch {
      setSessions([]);
    }

    try {
      const creds = await api<StoredCredential[]>("/api/credentials");
      setCredentials(creds);
    } catch {
      setCredentials([]);
    }
  }

  useEffect(() => {
    if (!viewer) return;
    loadAll(viewer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewer?.id]);

  async function saveName() {
    if (!viewer) return;
    setNameSaving(true);
    setNameErr(null);
    try {
      // Self-edit endpoint — members/managers can't PATCH /api/team/{self},
      // so use the dedicated auth/me route.
      await api(`/api/auth/me`, {
        method: "PATCH",
        json: { full_name: nameDraft.trim() || null },
      });
      await refresh();
      // Refresh the team view too so placement_count etc. stay consistent.
      const m = await api<TeamMember>(`/api/team/${viewer.id}`);
      setMember(m);
      setEditingName(false);
    } catch (err) {
      setNameErr(err instanceof ApiError ? err.message : "Failed to save");
    } finally {
      setNameSaving(false);
    }
  }

  if (loadErr) {
    return (
      <div className="w-full">
        <div className="card p-4 border-rose-500/25 bg-rose-500/[0.04] text-rose-300 text-sm">
          {loadErr}
        </div>
      </div>
    );
  }

  if (!viewer || !member) {
    return (
      <div className="w-full space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-32" />
        <Skeleton className="h-48" />
      </div>
    );
  }

  const lastSeenLabel = formatLastSeen(member.last_seen_at, member.is_online);

  return (
    <div className="w-full space-y-4">

      {/* Identity hero — accent-gradient banner (Home's hero treatment) with
          an integrated, evenly-divided stat band as its footer. */}
      <div className="card overflow-hidden border border-white/5 bg-gradient-to-br from-accent-500/[0.07] via-transparent to-transparent">
        <div className="p-6 flex flex-col sm:flex-row sm:items-center gap-5">
          {viewer.avatar_locked ? (
            /* Admin-pinned avatar — no picker, no hover overlay, no pencil
               badge, so nothing reads as editable. The API refuses the change
               anyway (403 avatar_locked). */
            <div className="flex-shrink-0 mx-auto sm:mx-0">
              <Avatar
                url={viewer.avatar_url}
                name={member.full_name}
                email={member.email}
                size="lg"
                online={member.is_online}
                userId={member.id}
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setAvatarOpen(true)}
              className="relative group/av rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 flex-shrink-0 mx-auto sm:mx-0"
              title={viewer.avatar_url ? "Change avatar" : "Pick a cartoon avatar"}
            >
              <Avatar
                url={viewer.avatar_url}
                name={member.full_name}
                email={member.email}
                size="lg"
                online={member.is_online}
                userId={member.id}
              />
              <span className="absolute inset-0 rounded-full bg-black/55 opacity-0 group-hover/av:opacity-100 transition flex items-center justify-center text-[10px] font-semibold text-white tracking-wider uppercase">
                Change
              </span>
              {/* Always-visible corner affordance so the avatar reads as editable
                  even without a hover. */}
              <span className="absolute -bottom-0.5 -right-0.5 h-6 w-6 rounded-full bg-accent-600 ring-2 ring-ink-900 flex items-center justify-center text-white shadow-sm">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 20h9" />
                  <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                </svg>
              </span>
            </button>
          )}

          <div className="flex-1 min-w-0 text-center sm:text-left">
            {editingName ? (
              <div className="flex items-center gap-2 flex-wrap sm:justify-start justify-center">
                <input
                  className="bg-ink-850 border border-white/15 rounded-md py-1 px-2 text-[17px] font-semibold text-slate-100 w-full sm:w-64 focus:outline-none focus:border-accent-500/60 focus:ring-2 focus:ring-accent-500/20"
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  disabled={nameSaving}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveName();
                    if (e.key === "Escape") {
                      setNameDraft(member.full_name || "");
                      setEditingName(false);
                    }
                  }}
                  placeholder="Your full name"
                />
                <button
                  onClick={saveName}
                  disabled={nameSaving}
                  className="text-[11.5px] font-medium px-2.5 py-1 rounded-md bg-accent-600 hover:bg-accent-500 text-white transition-colors disabled:opacity-50"
                >
                  {nameSaving ? "Saving…" : "Save"}
                </button>
                <button
                  onClick={() => {
                    setNameDraft(member.full_name || "");
                    setEditingName(false);
                    setNameErr(null);
                  }}
                  disabled={nameSaving}
                  className="text-[11.5px] text-slate-400 hover:text-slate-200 px-2 py-1"
                >
                  Cancel
                </button>
                <span className="text-[10px] text-slate-600 ml-1 hidden sm:inline">
                  ↵ save · Esc cancel
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2.5 flex-wrap justify-center sm:justify-start">
                <button
                  type="button"
                  onClick={() => setEditingName(true)}
                  title="Click to edit name"
                  className="group/name inline-flex items-center gap-1.5 rounded-md -mx-1 px-1 hover:bg-white/[0.04] transition min-w-0"
                >
                  <h2 className="text-2xl font-semibold tracking-[-0.01em] text-slate-100 leading-tight truncate">
                    {member.full_name || (
                      <span className="text-slate-500">— no name set —</span>
                    )}
                  </h2>
                  <span className="text-[10px] text-slate-600 group-hover/name:text-accent-300 transition flex-shrink-0">
                    ✎
                  </span>
                </button>
                <span className={`badge ${ROLE_STYLES[member.role]}`}>
                  {roleLabel(member.role)}
                </span>
                <span className={`badge ${STATUS_STYLES[member.status]}`}>
                  {member.status}
                </span>
              </div>
            )}

            <div className="mt-2 flex items-center gap-2 flex-wrap justify-center sm:justify-start text-[12.5px]">
              <span className="text-slate-400 font-mono truncate max-w-full">
                {member.email}
              </span>
              {lastSeenLabel && (
                <>
                  <span className="text-slate-600" aria-hidden>
                    ·
                  </span>
                  <span
                    className={`inline-flex items-center gap-1.5 ${
                      member.is_online ? "text-emerald-400" : "text-slate-500"
                    }`}
                  >
                    {member.is_online && (
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    )}
                    {lastSeenLabel}
                  </span>
                </>
              )}
            </div>
            <div className="text-[11.5px] text-slate-500 mt-1">
              {ROLE_DESCRIPTION[member.role]}
            </div>
            {nameErr && (
              <div className="text-[12px] text-rose-400 mt-2">{nameErr}</div>
            )}
          </div>
        </div>

        {/* Stat band — one even row on sm+ with hairline dividers; a tidy
            2-col grid on mobile (last cell spans full width). */}
        <div className="grid grid-cols-2 sm:grid-cols-5 border-t border-white/5 [&>*]:border-white/5 sm:[&>*:not(:first-child)]:border-l [&>*:last-child]:col-span-2 sm:[&>*:last-child]:col-span-1">
          <HeroStat label="Agency" value={viewer.agency.name} />
          <HeroStat label="Reports to" value={member.manager_name || "—"} />
          <HeroStat label="Joined" value={formatDateOnly(member.created_at)} />
          <HeroStat
            label="Last login"
            value={formatDateOnly(member.last_login_at)}
          />
          <HeroStat
            label="Placements"
            value={member.placement_count.toLocaleString()}
          />
        </div>
      </div>

      {/* Work + settings — two columns only at xl+ where the right rail has
          real room; below that everything stacks full-width so nothing
          cramps. Left: performance + activity. Right: portals + security. */}
      <div className="grid gap-4 xl:grid-cols-3">
        <div className="xl:col-span-2 space-y-4 min-w-0">
          <BenchmarkCard stats={benchmarks} title="This month" />
          <div className="mt-3"><ScoreCard /></div>

          {/* Recent placements */}
          <div className="card overflow-hidden">
        <SectionHeader
          title="Recent placements"
          subtitle={
            placementTotal === 0
              ? "You haven't published any listings yet."
              : `${placementTotal} total · 5 most recent`
          }
          right={
            placementTotal > 0 ? (
              <Link
                to={`/placements?user=${member.id}`}
                className="text-[12px] font-medium text-accent-400 hover:text-accent-300 whitespace-nowrap"
              >
                View all →
              </Link>
            ) : null
          }
        />
        {placements.length === 0 ? (
          <div className="px-5 py-4 text-[12.5px] text-slate-500">
            Publish your first listing from the{" "}
            <Link to="/publish" className="text-accent-400 hover:text-accent-300">
              Publish
            </Link>{" "}
            page to see it here.
          </div>
        ) : (
          <>
            {/* Mobile: stacked rows so each placement is one touch target
                instead of forcing a horizontal-scroll 6-col table. Capped so a
                long list scrolls inside the card instead of stretching it. */}
            <div className="md:hidden divide-y divide-white/5 max-h-[380px] overflow-y-auto">
              {placements.map((p) => (
                <div
                  key={`m-${p.id}`}
                  className="px-5 py-3 flex items-center gap-3"
                >
                  <PlatformBadge platform={p.platform} />
                  <div className="flex-1 min-w-0">
                    <div className="text-slate-200 font-mono tabular-nums text-[12px] truncate">
                      {p.external_id || "—"}
                    </div>
                    <div className="text-[10.5px] text-slate-500 truncate">
                      {p.district || "—"} · {formatDate(p.published_at)}
                    </div>
                  </div>
                  <div className="text-right whitespace-nowrap">
                    <div className="text-slate-200 tabular-nums text-[12.5px]">
                      {formatPrice(p)}
                    </div>
                    <a
                      href={p.published_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[10.5px] font-medium text-accent-400 hover:text-accent-300"
                    >
                      Open ↗
                    </a>
                  </div>
                </div>
              ))}
            </div>

            <div className="hidden md:block overflow-auto max-h-[380px]">
              <table className="w-full text-[12.5px]">
                <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-ink-900 border-b border-white/5 sticky top-0 z-10">
                  <tr>
                    <th className="text-left px-5 py-2.5 font-medium">Platform</th>
                    <th className="text-left px-5 py-2.5 font-medium">Listing ID</th>
                    <th className="text-left px-5 py-2.5 font-medium">District</th>
                    <th className="text-right px-5 py-2.5 font-medium">Price</th>
                    <th className="text-left px-5 py-2.5 font-medium">Published</th>
                    <th className="text-right px-5 py-2.5 font-medium">Link</th>
                  </tr>
                </thead>
                <tbody>
                  {placements.map((p) => (
                    <tr
                      key={p.id}
                      className="border-t border-white/5 hover:bg-white/[0.02]"
                    >
                      <td className="px-5 py-2.5">
                        <PlatformBadge platform={p.platform} />
                      </td>
                      <td className="px-5 py-2.5 font-mono tabular-nums text-slate-200">
                        {p.external_id || "—"}
                      </td>
                      <td className="px-5 py-2.5 text-slate-400">
                        {p.district || "—"}
                      </td>
                      <td className="px-5 py-2.5 text-slate-200 tabular-nums text-right">
                        {formatPrice(p)}
                      </td>
                      <td className="px-5 py-2.5 text-slate-500 text-[11px] whitespace-nowrap">
                        {formatShort(p.published_at)}
                      </td>
                      <td className="px-5 py-2.5 text-right">
                        <a
                          href={p.published_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[11.5px] font-medium text-accent-400 hover:text-accent-300"
                        >
                          Open ↗
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
          </div>
        </div>

        {/* Right column — portal connections + security. Both tall cards cap
            their scroll region so neither column can run away and break the
            layout; the columns stay bounded and read tidily side by side. */}
        <div className="space-y-4 min-w-0">
          <PortalConnectionsCard
            userId={viewer.id}
            credentials={credentials}
            onChange={() => viewer && loadAll(viewer)}
          />

          <div className="card overflow-hidden">
            <SectionHeader
              title="Security"
              subtitle="Password and active sessions."
            />
            <div className="px-5 py-4 space-y-4">
              <PasswordManager />
              <SessionsList
                sessions={sessions}
                onReload={() => viewer && loadAll(viewer)}
              />
            </div>
          </div>
        </div>
      </div>

      {avatarOpen && (
        <AvatarPickerModal
          currentUrl={viewer.avatar_url}
          name={member.full_name}
          email={member.email}
          onClose={() => setAvatarOpen(false)}
          onSave={saveAvatar}
        />
      )}
    </div>
  );
}


/** Generate N quirky seed strings for a style. We mix the user's email
 *  into the seed so the picker doesn't show the *exact* same 30 faces to
 *  every account — feels more personal even though it's deterministic. */
function buildSeeds(email: string, count: number, salt: number): string[] {
  const base = email.split("@")[0] || "agent";
  const seeds: string[] = [];
  for (let i = 0; i < count; i++) {
    seeds.push(`${base}-${salt}-${i}`);
  }
  return seeds;
}

export function AvatarPickerModal({
  currentUrl,
  name,
  email,
  onClose,
  onSave,
  required,
  onLogout,
}: {
  currentUrl: string | null;
  name: string | null;
  email: string;
  onClose: () => void;
  onSave: (url: string | null) => Promise<void>;
  /** Renders as an undismissable full-page gate instead of a closable
   *  modal — no backdrop-click-to-close, no × button. Used to force
   *  teammates without an avatar to pick one before they can use the
   *  app (see ``Layout.tsx``). */
  required?: boolean;
  /** Escape hatch shown only in ``required`` mode — wrong account, or
   *  just not ready to pick yet. Without this the gate would be a
   *  genuine dead end for anyone who can't/won't choose an avatar. */
  onLogout?: () => void;
}) {
  const [styleId, setStyleId] = useState<AvatarStyleId>(AVATAR_STYLES[0].id);
  const [salt, setSalt] = useState(0);
  const [saving, setSaving] = useState<string | null>(null);
  const seeds = useMemo(
    () => buildSeeds(email, 24, salt),
    [email, salt],
  );

  async function pick(url: string | null) {
    setSaving(url ?? "__clear__");
    try {
      await onSave(url);
    } finally {
      setSaving(null);
    }
  }

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${
        required ? "bg-ink-950" : "bg-black/65"
      }`}
      onClick={required ? undefined : onClose}
    >
      <div
        className="card max-w-3xl w-full p-5 space-y-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="card-title">
              {required ? "Pick an avatar to continue" : "Pick your avatar"}
            </div>
            <div className="text-[12px] text-slate-500 mt-0.5">
              {required
                ? "Every teammate needs an avatar — pick a vibe, then a face. Tap to save and continue."
                : "Cartoon-style profile pictures · pick a vibe, then a face. Tap to save instantly."}
            </div>
          </div>
          {!required && (
            <button
              onClick={onClose}
              className="text-slate-500 hover:text-slate-200 text-lg leading-none px-2"
              aria-label="close"
            >
              ×
            </button>
          )}
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mr-1">
            Style
          </div>
          {AVATAR_STYLES.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setStyleId(s.id)}
              className={
                "text-xs px-2.5 py-1 rounded-full border transition " +
                (styleId === s.id
                  ? "border-accent-500/50 bg-accent-500/15 text-accent-200"
                  : "border-white/10 bg-white/[0.02] text-slate-400 hover:bg-white/[0.06]")
              }
            >
              {s.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setSalt((s) => s + 1)}
            className="text-xs px-2.5 py-1 rounded-full border border-white/10 bg-white/[0.02] text-slate-300 hover:bg-white/[0.06] ml-auto"
            title="Show different faces"
          >
            ↻ Shuffle
          </button>
        </div>

        <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3">
          {seeds.map((seed) => {
            const url = avatarUrl(styleId, seed);
            const isCurrent = url === currentUrl;
            const isSaving = saving === url;
            return (
              <button
                key={seed}
                type="button"
                onClick={() => pick(url)}
                disabled={!!saving}
                className={
                  "relative rounded-full overflow-hidden border-2 transition " +
                  (isCurrent
                    ? "border-accent-400 ring-2 ring-accent-500/40"
                    : "border-white/5 hover:border-accent-500/40") +
                  (saving ? " opacity-60 cursor-wait" : " hover:scale-105")
                }
                title={isCurrent ? "Current avatar" : "Use this avatar"}
              >
                <img
                  src={url}
                  alt={`avatar option`}
                  className="w-full aspect-square bg-white"
                  loading="lazy"
                />
                {isSaving && (
                  <span className="absolute inset-0 bg-black/45 flex items-center justify-center text-[10px] font-semibold text-white">
                    Saving…
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-white/5">
          <div className="text-[11px] text-slate-500">
            Powered by{" "}
            <a
              href="https://www.dicebear.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent-400/80 hover:text-accent-300"
            >
              DiceBear
            </a>{" "}
            · {AVATAR_STYLES.length} styles · pick anything, change anytime.
          </div>
          {currentUrl && (
            <button
              onClick={() => pick(null)}
              disabled={!!saving}
              className="text-xs text-rose-300/80 hover:text-rose-200 px-3 py-1.5 rounded border border-rose-500/20 hover:bg-rose-500/10"
              title="Go back to your initials"
            >
              {saving === "__clear__" ? "Clearing…" : "Use initials instead"}
            </button>
          )}
        </div>

        {required && onLogout && (
          <div className="text-center">
            <button
              type="button"
              onClick={onLogout}
              className="text-[11px] text-slate-500 hover:text-slate-300 underline-offset-2 hover:underline"
            >
              Not you? Log out
            </button>
          </div>
        )}

        {/* Suppress unused-variable warning when name is empty: rendered as
            a sr-only fallback label for screen readers. */}
        <span className="sr-only">{name || email}</span>
      </div>
    </div>
  );
}

/** Compact metadata tile — the Home stat-tile look. */
/** One cell of the hero stat band — label over value, single-line, aligned. */
function HeroStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="px-5 py-3.5 min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
        {label}
      </div>
      <div
        className="mt-1 text-[14px] font-semibold text-slate-100 truncate"
        title={value}
      >
        {value}
      </div>
    </div>
  );
}

/** Centered modal shell — header (icon + title/subtitle + close), scrollable
 *  body, optional sticky footer. Shared by the credential + password wizards. */
function ModalShell({
  title,
  subtitle,
  icon,
  onClose,
  children,
  footer,
}: {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/65"
      onClick={onClose}
    >
      <div
        className="card w-full max-w-md overflow-hidden max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 px-5 py-4 border-b border-white/5">
          {icon}
          <div className="min-w-0 flex-1">
            <div className="text-[15px] font-semibold text-slate-100 leading-tight">
              {title}
            </div>
            {subtitle && (
              <div className="text-[12px] text-slate-500 mt-0.5">{subtitle}</div>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-200 text-xl leading-none px-1 -mt-0.5"
            aria-label="close"
          >
            ×
          </button>
        </div>
        <div className="px-5 py-4 overflow-y-auto">{children}</div>
        {footer && (
          <div className="px-5 py-3 border-t border-white/5 bg-white/[0.015] flex items-center gap-2 justify-end">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

/** Inline success/error banner used across the wizards. */
function ResultBanner({ ok, children }: { ok: boolean; children: ReactNode }) {
  return (
    <div
      className={
        "text-[12px] rounded-lg px-3 py-2 border " +
        (ok
          ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/20"
          : "text-rose-400 bg-rose-500/10 border-rose-500/20")
      }
    >
      {ok ? "✓ " : "✗ "}
      {children}
    </div>
  );
}

/** Add / manage / validate one portal's publishing credentials, as a modal
 *  wizard. Manage view (existing creds) → Test / Update / Remove. Edit view
 *  (new or "Update") → email/password[/phone] form with a test-before-save
 *  step. All the network logic is identical to the old inline card. */
function CredentialWizardModal({
  platform,
  existing,
  requirePhone = false,
  onClose,
  onSaved,
}: {
  platform: Platform;
  existing: StoredCredential | undefined;
  requirePhone?: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const label = platformLabel(platform);
  const [editing, setEditing] = useState(!existing);
  const [email, setEmail] = useState(existing?.email || "");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<
    { ok: boolean; message: string } | null
  >(null);
  const [err, setErr] = useState<string | null>(null);
  const [savedOk, setSavedOk] = useState(false);

  async function save(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setSavedOk(false);
    setTestResult(null);
    setBusy(true);
    try {
      const body: Record<string, unknown> = { email, password };
      if (requirePhone) body.phone = phone;
      await api(`/api/credentials/${platform}`, { method: "PUT", json: body });
      setPassword("");
      setPhone("");
      setSavedOk(true);
      setEditing(false);
      onSaved();
    } catch (error) {
      setErr(error instanceof Error ? error.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function runTest() {
    setTesting(true);
    setTestResult(null);
    setErr(null);
    setSavedOk(false);
    try {
      // Editing → test the typed values without persisting. Managing saved
      // creds → omit body so the backend uses the stored ones.
      const body: Record<string, unknown> = {};
      if (editing) {
        body.email = email;
        body.password = password;
        if (requirePhone) body.phone = phone;
      }
      const res = await api<{ ok: boolean; code: string; message: string }>(
        `/api/credentials/${platform}/test`,
        { method: "POST", json: body },
      );
      setTestResult({ ok: res.ok, message: res.message });
    } catch (error) {
      setTestResult({
        ok: false,
        message: error instanceof Error ? error.message : "Test failed",
      });
    } finally {
      setTesting(false);
    }
  }

  async function remove() {
    if (!confirm(`Remove ${label} credentials?`)) return;
    await api(`/api/credentials/${platform}`, { method: "DELETE" });
    onSaved();
    onClose();
  }

  const canTestTyped =
    !!email && !!password && (!requirePhone || !!phone.trim());

  const icon = (
    <div className="h-10 w-10 rounded-xl bg-white/[0.03] border border-white/5 flex items-center justify-center flex-shrink-0">
      <PlatformLogo platform={platform} size="sm" />
    </div>
  );

  return (
    <ModalShell
      icon={icon}
      title={`${existing ? "Manage" : "Connect"} ${label}`}
      subtitle="Encrypted at rest · decrypted only at publish."
      onClose={onClose}
    >
      {existing && !editing ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-white/5 bg-white/[0.02] divide-y divide-white/5">
            <div className="flex items-center justify-between gap-3 px-4 py-2.5">
              <span className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">
                Account
              </span>
              <span className="font-medium text-slate-200 font-mono text-[12.5px] truncate">
                {existing.email}
              </span>
            </div>
            {requirePhone && (
              <div className="flex items-center justify-between gap-3 px-4 py-2.5">
                <span className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">
                  Phone
                </span>
                <span className="text-[12.5px] text-slate-400">
                  {existing.has_phone ? "Saved (hidden)" : "Not set"}
                </span>
              </div>
            )}
          </div>

          {testResult && (
            <ResultBanner ok={testResult.ok}>{testResult.message}</ResultBanner>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={runTest}
              disabled={testing}
              className="btn-secondary flex-1 !py-2 !text-xs"
              title="Verify the saved credentials work with the portal"
            >
              {testing ? "Testing…" : "Test connection"}
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(true);
                setTestResult(null);
              }}
              className="btn-secondary flex-1 !py-2 !text-xs"
            >
              Update credentials
            </button>
          </div>
          <button
            type="button"
            onClick={remove}
            className="w-full text-[12px] text-rose-300/80 hover:text-rose-200 py-2 rounded-lg border border-rose-500/20 hover:bg-rose-500/10 transition-colors"
          >
            Remove connection
          </button>
        </div>
      ) : (
        <form onSubmit={save} className="space-y-3">
          <div>
            <label className="label !mb-1">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input !py-2"
              autoFocus
            />
          </div>
          <div>
            <label className="label !mb-1">Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input !py-2"
              placeholder="••••••••"
            />
          </div>
          {requirePhone && (
            <div>
              <label className="label !mb-1">Phone</label>
              <input
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="input !py-2"
                placeholder="5XXXXXXXX"
              />
            </div>
          )}
          {err && <ResultBanner ok={false}>{err}</ResultBanner>}
          {savedOk && <ResultBanner ok>Connected.</ResultBanner>}
          {testResult && (
            <ResultBanner ok={testResult.ok}>{testResult.message}</ResultBanner>
          )}
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={runTest}
              disabled={!canTestTyped || testing}
              className="btn-secondary !py-2 !text-xs"
              title={
                canTestTyped
                  ? "Try these credentials against the portal without saving"
                  : "Fill in email, password" +
                    (requirePhone ? " and phone" : "") +
                    " to test"
              }
            >
              {testing ? "Testing…" : "Test"}
            </button>
            <button
              type="submit"
              disabled={busy}
              className="btn-primary flex-1 !py-2 !text-xs"
            >
              {busy ? "Saving…" : existing ? "Save changes" : "Connect account"}
            </button>
          </div>
          {existing && (
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setPassword("");
                setPhone("");
                setErr(null);
                setTestResult(null);
              }}
              className="w-full text-[12px] text-slate-400 hover:text-slate-200 py-1.5"
            >
              Cancel
            </button>
          )}
        </form>
      )}
    </ModalShell>
  );
}

/** One portal row — brand + live balance / status on the left, a
 *  Manage/Connect button on the right. Horizontal so it stays legible in a
 *  narrow rail (no squeezed side-by-side cards). */
function PortalConnectionCard({
  platform,
  connected,
  balance,
  loading,
  onManage,
}: {
  platform: Platform;
  connected: boolean;
  balance: PortalBalance | null;
  loading: boolean;
  onManage: () => void;
}) {
  return (
    <div className="rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3 flex items-center gap-3">
      <div className="w-14 flex-shrink-0 flex items-center">
        <PlatformLogo platform={platform} size="sm" />
      </div>
      <div className="flex-1 min-w-0">
        {!connected ? (
          <>
            <div className="text-[13px] font-medium text-slate-300 leading-tight">
              {platformLabel(platform)}
            </div>
            <div className="text-[11px] text-slate-500">Not linked</div>
          </>
        ) : loading || balance == null ? (
          <Skeleton className="h-6 w-24" />
        ) : balance.ok ? (
          <>
            <div className="text-[16px] font-semibold tabular-nums text-slate-100 leading-tight">
              {formatBalance(balance)}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              {platformLabel(platform)} balance
            </div>
          </>
        ) : (
          <>
            <div className="text-[13px] font-medium text-slate-300 leading-tight">
              {platformLabel(platform)}
            </div>
            <div className="text-[11px] text-rose-400 leading-snug">
              {balanceErrorLabel(balance.error)}
            </div>
          </>
        )}
      </div>
      <button
        type="button"
        onClick={onManage}
        className="btn-secondary !py-1.5 !px-3 !text-xs flex-shrink-0"
      >
        {connected ? "Manage" : "Connect"}
      </button>
    </div>
  );
}

/** Portal connections section — merges the old "Portal balances" and "Linked
 *  publishing accounts" cards into two compact side-by-side cards, each with a
 *  wizard modal for adding / managing / validating credentials. Self-only
 *  (credential editing); EmployeeDetail keeps the read-only PortalBalanceCard. */
function PortalConnectionsCard({
  userId,
  credentials,
  onChange,
}: {
  userId: string;
  credentials: StoredCredential[];
  onChange: () => void;
}) {
  const [balances, setBalances] = useState<PortalBalances | null>(null);
  const [loading, setLoading] = useState(true);
  const [wizard, setWizard] = useState<Platform | null>(null);

  const load = useCallback(
    async (force: boolean) => {
      setLoading(true);
      try {
        const res = await api<PortalBalances>(
          `/api/team/${userId}/portal-balances${force ? "?refresh=true" : ""}`,
        );
        setBalances(res);
      } catch {
        setBalances(null);
      } finally {
        setLoading(false);
      }
    },
    [userId],
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  const myhomeCred = credentials.find((c) => c.platform === "myhome");
  const ssCred = credentials.find((c) => c.platform === "ss");

  return (
    <div className="card overflow-hidden">
      <SectionHeader
        title="Portal connections"
        subtitle="Publishing accounts + live wallet balance"
        right={
          <button
            type="button"
            onClick={() => void load(true)}
            disabled={loading}
            className="text-[12px] text-slate-300 hover:text-slate-100 disabled:opacity-40 transition-colors"
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
        }
      />
      <div className="p-4 space-y-2.5">
        <PortalConnectionCard
          platform="myhome"
          connected={!!myhomeCred}
          balance={balances?.myhome ?? null}
          loading={loading && balances == null}
          onManage={() => setWizard("myhome")}
        />
        <PortalConnectionCard
          platform="ss"
          connected={!!ssCred}
          balance={balances?.ss ?? null}
          loading={loading && balances == null}
          onManage={() => setWizard("ss")}
        />
      </div>

      {wizard && (
        <CredentialWizardModal
          platform={wizard}
          existing={credentials.find((c) => c.platform === wizard)}
          requirePhone={wizard === "myhome"}
          onClose={() => setWizard(null)}
          onSaved={() => {
            onChange();
            void load(true);
          }}
        />
      )}
    </div>
  );
}

/** Compact password row + the change-password wizard modal. */
function PasswordManager() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="flex items-center justify-between gap-3 rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-9 w-9 rounded-xl bg-slate-500/10 text-slate-300 flex items-center justify-center flex-shrink-0">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium text-slate-200">Password</div>
            <div className="text-[11.5px] text-slate-500 tracking-widest">
              ••••••••••
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="btn-secondary !px-3 !py-1.5 !text-xs flex-shrink-0"
        >
          Change
        </button>
      </div>
      {open && <ChangePasswordModal onClose={() => setOpen(false)} />}
    </>
  );
}

function ChangePasswordModal({ onClose }: { onClose: () => void }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const tooShort = next.length > 0 && next.length < 8;
  const mismatch = confirm.length > 0 && next !== confirm;
  const disabled = busy || !current || next.length < 8 || next !== confirm;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setOk(false);
    if (next.length < 8) {
      setErr("New password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      setErr("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await api("/api/auth/change-password", {
        method: "POST",
        json: { current_password: current, new_password: next },
      });
      setCurrent("");
      setNext("");
      setConfirm("");
      setOk(true);
    } catch (error) {
      if (error instanceof ApiError && error.message === "invalid_current_password") {
        setErr("Current password is incorrect.");
      } else if (error instanceof ApiError && error.message === "new_password_same_as_current") {
        setErr("Your new password must be different from the current one.");
      } else {
        setErr(error instanceof Error ? error.message : "Failed to change password");
      }
    } finally {
      setBusy(false);
    }
  }

  const icon = (
    <div className="h-10 w-10 rounded-xl bg-accent-500/10 text-accent-300 flex items-center justify-center flex-shrink-0">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
    </div>
  );

  return (
    <ModalShell
      icon={icon}
      title="Change password"
      subtitle="Signs out every other device."
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary !px-3 !py-1.5 !text-xs"
          >
            {ok ? "Close" : "Cancel"}
          </button>
          <button
            type="submit"
            form="change-password-form"
            disabled={disabled}
            className="btn-primary !px-3 !py-1.5 !text-xs"
          >
            {busy ? "Saving…" : "Update password"}
          </button>
        </>
      }
    >
      {ok ? (
        <ResultBanner ok>
          Password updated. Every other device has been signed out.
        </ResultBanner>
      ) : (
        <form id="change-password-form" onSubmit={submit} className="space-y-3">
          <div>
            <label className="label !mb-1">Current password</label>
            <input
              type="password"
              className="input !py-2"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              autoFocus
            />
          </div>
          <div>
            <label className="label !mb-1">New password</label>
            <input
              type="password"
              className="input !py-2"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              placeholder="at least 8 characters"
            />
            {tooShort && (
              <div className="text-[11px] text-amber-400/80 mt-1">
                At least 8 characters.
              </div>
            )}
          </div>
          <div>
            <label className="label !mb-1">Confirm new password</label>
            <input
              type="password"
              className="input !py-2"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
            />
            {mismatch && (
              <div className="text-[11px] text-rose-400/90 mt-1">
                Passwords don't match.
              </div>
            )}
          </div>
          {err && <ResultBanner ok={false}>{err}</ResultBanner>}
        </form>
      )}
    </ModalShell>
  );
}

function SessionsList({
  sessions,
  onReload,
}: {
  sessions: TeamSession[];
  onReload: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const otherCount = useMemo(() => sessions.filter((s) => !s.current).length, [sessions]);

  async function signOutOthers() {
    if (otherCount === 0) return;
    if (
      !confirm(
        `Sign out of ${otherCount} other ${otherCount === 1 ? "device" : "devices"}? You'll stay signed in here.`,
      )
    )
      return;
    setBusy(true);
    setErr(null);
    setNote(null);
    try {
      const r = await api<{ signed_out: number }>(`/api/auth/sessions/others`, {
        method: "POST",
      });
      setNote(`Signed out of ${r.signed_out} ${r.signed_out === 1 ? "device" : "devices"}.`);
      onReload();
    } catch (error) {
      setErr(error instanceof Error ? error.message : "Failed to sign out other devices");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            Recent logins
          </div>
          <div className="text-[12px] text-slate-500">
            {sessions.length === 0
              ? "No login history yet."
              : `${sessions.length} recent — this device is highlighted.`}
          </div>
        </div>
        {otherCount > 0 && (
          <button
            onClick={signOutOthers}
            disabled={busy}
            className="btn-secondary text-xs"
          >
            {busy
              ? "Signing out…"
              : `Sign out other ${otherCount === 1 ? "device" : `devices (${otherCount})`}`}
          </button>
        )}
      </div>

      {note && (
        <div className="text-[12px] text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2">
          {note}
        </div>
      )}
      {err && (
        <div className="text-[12px] text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2">
          {err}
        </div>
      )}

      {sessions.length > 0 && (
        <div className="rounded-lg border border-white/5 divide-y divide-white/5 overflow-y-auto max-h-[316px]">
          {sessions.map((s) => (
            <div
              key={s.id}
              className="flex items-start justify-between gap-3 px-3.5 py-2.5"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[12.5px] text-slate-300 truncate">
                    {shortUA(s.user_agent)}
                  </span>
                  {s.current && (
                    <span className="badge border-accent-500/30 bg-accent-500/10 text-accent-300 text-[10px]">
                      this device
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 font-mono tabular-nums mt-0.5">
                  {s.ip || "—"}
                </div>
              </div>
              <div className="text-right flex-shrink-0 text-[11px] leading-tight">
                <div className="text-slate-400 whitespace-nowrap">
                  {formatShort(s.created_at)}
                </div>
                <div className="text-slate-600 whitespace-nowrap mt-0.5">
                  ends {formatShort(s.expires_at)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
